// src/Features/Authentication/authSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { loginUser, getUserPermissions, getAuthBaseUrl } from '../API/api';
import {
  initializeWebSockets,
  disconnectWebSockets,
} from '../../shared/services/websocketServices';
import * as tokenStorage from '../../shared/utils/tokenStorage';

// Per-session active organisation. Deliberately sessionStorage, not
// tokenStorage: the latter follows "Remember Me" into localStorage, which
// would leak one tab's organisation choice into every other tab.
const ACTIVE_ORG_STORAGE_KEY = 'activeOrganization';

// Auth Base URL — centralized in api.js (handles dev/staging/production)
const AUTH_BASE_URL = getAuthBaseUrl();

export const login = createAsyncThunk(
  'auth/login',
  async ({ username, password, rememberMe }, { rejectWithValue, dispatch }) => {
    try {
      const response = await loginUser({ username, password });
      console.log('Login response:', response.data);

      // JWT token endpoint returns access, refresh tokens and user data
      const accessToken = response.data.access;
      const refreshToken = response.data.refresh;
      const userData = response.data.user || {};
      const organizations = response.data.organizations || [];

      // Set remember me preference before storing tokens
      if (rememberMe !== undefined) {
        tokenStorage.setRememberMe(rememberMe);
      }

      // Store the tokens for future API calls
      tokenStorage.setToken(accessToken);
      tokenStorage.setRefreshToken(refreshToken);

      // Now fetch user permissions to get RBAC data
      try {
        const permissionsResponse = await getUserPermissions();
        console.log('Full permissions response:', permissionsResponse);
        console.log('Permissions response data:', permissionsResponse.data);

        // The user-info endpoint returns the complete user data
        const userInfo = permissionsResponse.data;

        // Calculate max role level from roles
        const maxRoleLevel =
          userInfo.roles && userInfo.roles.length > 0
            ? Math.max(...userInfo.roles.map((r) => r.hierarchy_level || 0))
            : userInfo.is_root_admin
              ? 999
              : 0; // Root admin gets highest level

        // Extract permission codes for easier checking
        const permissionCodes = userInfo.permissions
          ? userInfo.permissions.map((p) => (typeof p === 'string' ? p : p.code))
          : [];

        // Pick the organisation flagged is_primary rather than whichever happens
        // to sort first — loadUserPermissions already does this, and the two
        // disagreeing meant a refresh could silently change the active org.
        const availableOrgs = userInfo.organizations || organizations;
        const primaryOrg = availableOrgs.find((org) => org.is_primary) || availableOrgs[0] || null;

        return {
          token: accessToken,
          refreshToken: refreshToken,
          user: {
            ...userData,
            ...userInfo, // Merge all user info
            // Add computed RBAC fields
            roles: userInfo.roles || [],
            max_role_level: maxRoleLevel,
            organizations: availableOrgs,
            current_organization: primaryOrg,
          },
          organizations: availableOrgs,
          defaultOrganization: primaryOrg,
          permissions: permissionCodes,
          roles: userInfo.roles || [],
          maxRoleLevel: maxRoleLevel,
          currentOrganization: primaryOrg,
        };
      } catch (permError) {
        // If permissions fetch fails, still login with basic data
        console.warn('Failed to fetch permissions:', permError);
        return {
          token: accessToken,
          refreshToken: refreshToken,
          user: userData,
          organizations: organizations,
          defaultOrganization: response.data.default_organization || null,
          permissions: [],
          roles: [],
          maxRoleLevel: 0,
          currentOrganization: null,
        };
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail ||
          error.response?.data?.error ||
          error.message ||
          'Login failed'
      );
    }
  }
);

// Switch organization context
export const switchOrganization = createAsyncThunk(
  'auth/switchOrganization',
  async (organizationId, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.auth.token;

      const response = await fetch(`${AUTH_BASE_URL}switch-organization/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ organization_id: organizationId }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Organization switch failed');
      }

      const data = await response.json();
      return data.current_organization;
    } catch (error) {
      return rejectWithValue(error.message || 'Organization switch failed');
    }
  }
);

// Load user permissions for current organization
export const loadUserPermissions = createAsyncThunk(
  'auth/loadUserPermissions',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.auth.token;

      const response = await fetch(`${AUTH_BASE_URL}auth/user-info/`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to load permissions');
      }

      const data = await response.json();
      // Derive maxRoleLevel from highest hierarchy_level in roles
      const maxLevel = (data.roles || []).reduce(
        (max, role) => Math.max(max, role.hierarchy_level || 0),
        0
      );
      // Current organization is the one marked is_primary
      const currentOrg = (data.organizations || []).find((org) => org.is_primary) || null;
      return {
        roles: data.roles || [],
        permissions: data.permissions || [],
        maxRoleLevel: maxLevel,
        currentOrganization: currentOrg,
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load permissions');
    }
  }
);

// Add updateUser thunk for profile updates
export const updateUser = createAsyncThunk(
  'auth/updateUser',
  async (userData, { rejectWithValue }) => {
    try {
      // This would typically be an API call to update the user profile
      // Replace with actual API call when available
      const response = await mockUpdateUserApi(userData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Profile update failed');
    }
  }
);

// Mock API call - replace with actual API call
const mockUpdateUserApi = async (userData) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        data: userData,
        success: true,
      });
    }, 500);
  });
};

// Function removed - was unused
// makeSerializable functionality is now inline where needed

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    token: tokenStorage.getItem('token') || null,
    refreshToken: tokenStorage.getItem('refreshToken') || null,
    user: (() => {
      try {
        const userData = tokenStorage.getItem('user');
        return userData && userData !== 'undefined' ? JSON.parse(userData) : null;
      } catch {
        return null;
      }
    })(),
    organizations: (() => {
      try {
        const orgData = tokenStorage.getItem('organizations');
        return orgData && orgData !== 'undefined' ? JSON.parse(orgData) : [];
      } catch {
        return [];
      }
    })(),
    // The active organisation is deliberately PER SESSION. sessionStorage is read
    // first so a choice made in this tab does not leak to other tabs or survive a
    // browser restart, even for "Remember Me" users (tokenStorage falls back to
    // localStorage for them). tokenStorage remains the fallback so existing
    // sessions keep their organisation on first load after this ships.
    currentOrganization: (() => {
      try {
        const sessionOrg = sessionStorage.getItem(ACTIVE_ORG_STORAGE_KEY);
        if (sessionOrg && sessionOrg !== 'undefined') {
          return JSON.parse(sessionOrg);
        }
      } catch {
        // fall through to the persisted value
      }
      try {
        const currentOrgData = tokenStorage.getItem('currentOrganization');
        return currentOrgData && currentOrgData !== 'undefined' ? JSON.parse(currentOrgData) : null;
      } catch {
        return null;
      }
    })(),
    roles: (() => {
      try {
        const rolesData = tokenStorage.getItem('roles');
        return rolesData && rolesData !== 'undefined' ? JSON.parse(rolesData) : [];
      } catch {
        return [];
      }
    })(),
    permissions: (() => {
      try {
        const permissionsData = tokenStorage.getItem('permissions');
        return permissionsData && permissionsData !== 'undefined'
          ? JSON.parse(permissionsData)
          : [];
      } catch {
        return [];
      }
    })(),
    maxRoleLevel: (() => {
      const level = tokenStorage.getItem('maxRoleLevel');
      return level ? parseInt(level, 10) : 0;
    })(),
    // Impersonation state
    isImpersonating: (() => {
      try {
        const impersonating = tokenStorage.getItem('isImpersonating');
        return impersonating === 'true';
      } catch {
        return false;
      }
    })(),
    originalUser: (() => {
      try {
        const originalUserData = tokenStorage.getItem('originalUser');
        return originalUserData && originalUserData !== 'undefined'
          ? JSON.parse(originalUserData)
          : null;
      } catch {
        return null;
      }
    })(),
    originalToken: tokenStorage.getItem('originalToken') || null,
    originalRefreshToken: tokenStorage.getItem('originalRefreshToken') || null,
    impersonatedBy: (() => {
      try {
        const impersonatedByData = tokenStorage.getItem('impersonatedBy');
        return impersonatedByData && impersonatedByData !== 'undefined'
          ? JSON.parse(impersonatedByData)
          : null;
      } catch {
        return null;
      }
    })(),
    error: null,
    status: 'idle',
    updateStatus: 'idle',
    permissionsStatus: 'idle',
    orgSwitchStatus: 'idle',
  },
  reducers: {
    logout(state) {
      try {
        sessionStorage.removeItem(ACTIVE_ORG_STORAGE_KEY);
      } catch {
        // storage unavailable; nothing to clear
      }
      // Disconnect all WebSocket services before clearing user data
      disconnectWebSockets();

      state.token = null;
      state.refreshToken = null;
      state.user = null;
      state.organizations = [];
      state.currentOrganization = null;
      state.roles = [];
      state.permissions = [];
      state.maxRoleLevel = 0;
      state.isImpersonating = false;
      state.originalUser = null;
      state.originalToken = null;
      state.originalRefreshToken = null;
      state.impersonatedBy = null;
      // Clear all auth data from both sessionStorage and localStorage
      tokenStorage.clearAll();
    },
    setCredentials(state, action) {
      // Update all auth credentials (used for token refresh)
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken || state.refreshToken;
      state.user = action.payload.user || state.user;
      state.organizations = action.payload.organizations || state.organizations;
      state.defaultOrganization = action.payload.defaultOrganization || state.defaultOrganization;
      state.currentOrganization = action.payload.currentOrganization || state.currentOrganization;
      state.roles = action.payload.roles || state.roles;
      state.permissions = action.payload.permissions || state.permissions;
      state.maxRoleLevel =
        action.payload.maxRoleLevel !== undefined
          ? action.payload.maxRoleLevel
          : state.maxRoleLevel;

      // Update session storage only (for auto-logout on browser close)
      if (action.payload.token) {
        tokenStorage.setItem('token', action.payload.token);
      }
      if (action.payload.refreshToken) {
        tokenStorage.setItem('refreshToken', action.payload.refreshToken);
      }
      if (action.payload.user) {
        tokenStorage.setItem('user', JSON.stringify(action.payload.user));
      }
      if (action.payload.organizations) {
        tokenStorage.setItem('organizations', JSON.stringify(action.payload.organizations));
      }
      if (action.payload.currentOrganization) {
        tokenStorage.setItem(
          'currentOrganization',
          JSON.stringify(action.payload.currentOrganization)
        );
      }
      if (action.payload.roles) {
        tokenStorage.setItem('roles', JSON.stringify(action.payload.roles));
      }
      if (action.payload.permissions) {
        tokenStorage.setItem('permissions', JSON.stringify(action.payload.permissions));
      }
      if (action.payload.maxRoleLevel !== undefined) {
        tokenStorage.setItem('maxRoleLevel', action.payload.maxRoleLevel);
      }
    },
    // Add a simple sync action for immediate UI updates
    updateUserProfile(state, action) {
      // Ensure the user data is serializable
      const userData = { ...action.payload };
      if (userData.avatarIcon) {
        delete userData.avatarIcon;
      }

      state.user = { ...state.user, ...userData };
      tokenStorage.setItem('user', JSON.stringify(state.user));
    },
    // Clear permissions (useful when switching organizations)
    clearPermissions(state) {
      state.roles = [];
      state.permissions = [];
      state.maxRoleLevel = 0;
      state.permissionsStatus = 'idle';
    },
    // Update current organization without API call.
    // Written to sessionStorage so the choice is scoped to this tab/session, and
    // mirrored into tokenStorage so a full reload still has a sensible default.
    setCurrentOrganization(state, action) {
      state.currentOrganization = action.payload;
      try {
        sessionStorage.setItem(ACTIVE_ORG_STORAGE_KEY, JSON.stringify(action.payload));
      } catch {
        // Private-mode / storage-disabled: the Redux value still applies for this page.
      }
      tokenStorage.setItem('currentOrganization', JSON.stringify(action.payload));
    },
    // Set portal context for super admin switching
    setPortalContext(state, action) {
      state.portalContext = action.payload;
      state.isSuperAdminMode = action.payload?.isSuperAdmin || false;
    },
    // Clear portal context
    clearPortalContext(state) {
      state.portalContext = null;
      state.isSuperAdminMode = false;
    },
    // Start impersonating a user
    startImpersonation(state, action) {
      // Save current user session
      state.originalUser = state.user;
      state.originalToken = state.token;
      state.originalRefreshToken = state.refreshToken;
      state.isImpersonating = true;
      state.impersonatedBy = action.payload.impersonated_by;

      // Set impersonated user session
      state.token = action.payload.access;
      state.refreshToken = action.payload.refresh;
      state.user = action.payload.user;
      state.organizations = action.payload.user.organizations || [];
      state.currentOrganization = action.payload.user.organizations?.[0] || null;
      state.roles = action.payload.user.roles || [];
      state.permissions = action.payload.permissions || [];

      // Calculate max role level
      const maxLevel =
        action.payload.user.roles && action.payload.user.roles.length > 0
          ? Math.max(...action.payload.user.roles.map((r) => r.hierarchy_level || 0))
          : 0;
      state.maxRoleLevel = maxLevel;

      // Store in sessionStorage
      tokenStorage.setItem('isImpersonating', 'true');
      tokenStorage.setItem('originalUser', JSON.stringify(state.originalUser));
      tokenStorage.setItem('originalToken', state.originalToken);
      tokenStorage.setItem('originalRefreshToken', state.originalRefreshToken);
      tokenStorage.setItem('impersonatedBy', JSON.stringify(state.impersonatedBy));
      tokenStorage.setItem('token', state.token);
      tokenStorage.setItem('refreshToken', state.refreshToken);
      tokenStorage.setItem('user', JSON.stringify(state.user));
      tokenStorage.setItem('organizations', JSON.stringify(state.organizations));
      tokenStorage.setItem('currentOrganization', JSON.stringify(state.currentOrganization));
      tokenStorage.setItem('roles', JSON.stringify(state.roles));
      tokenStorage.setItem('permissions', JSON.stringify(state.permissions));
      tokenStorage.setItem('maxRoleLevel', maxLevel);
    },
    // End impersonation and restore original session
    endImpersonation(state) {
      if (!state.isImpersonating || !state.originalUser) {
        return;
      }

      // Restore original user session
      state.token = state.originalToken;
      state.refreshToken = state.originalRefreshToken;
      state.user = state.originalUser;
      state.isImpersonating = false;
      state.originalUser = null;
      state.originalToken = null;
      state.originalRefreshToken = null;
      state.impersonatedBy = null;

      // Restore original organizations and roles (from originalUser)
      state.organizations = state.user.organizations || [];
      state.currentOrganization = state.user.current_organization || null;
      state.roles = state.user.roles || [];
      state.permissions = [];
      state.maxRoleLevel = state.user.max_role_level || 0;

      // Update sessionStorage
      tokenStorage.setItem('token', state.token);
      tokenStorage.setItem('refreshToken', state.refreshToken);
      tokenStorage.setItem('user', JSON.stringify(state.user));
      tokenStorage.setItem('organizations', JSON.stringify(state.organizations));
      tokenStorage.setItem('currentOrganization', JSON.stringify(state.currentOrganization));
      tokenStorage.setItem('roles', JSON.stringify(state.roles));
      tokenStorage.setItem('permissions', JSON.stringify(state.permissions));
      tokenStorage.setItem('maxRoleLevel', state.maxRoleLevel);

      // Clear impersonation data
      tokenStorage.removeItem('isImpersonating');
      tokenStorage.removeItem('originalUser');
      tokenStorage.removeItem('originalToken');
      tokenStorage.removeItem('originalRefreshToken');
      tokenStorage.removeItem('impersonatedBy');
    },
  },
  extraReducers: (builder) => {
    builder
      // Login reducers
      .addCase(login.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        state.organizations = action.payload.organizations;
        state.currentOrganization = action.payload.currentOrganization;
        state.roles = action.payload.roles;
        state.permissions = action.payload.permissions;
        state.maxRoleLevel = action.payload.maxRoleLevel;

        // Ensure user data is serializable
        const userData = { ...action.payload.user };
        if (userData.avatarIcon) {
          delete userData.avatarIcon;
        }

        state.user = userData;
        tokenStorage.setItem('token', action.payload.token);
        tokenStorage.setItem('refreshToken', action.payload.refreshToken);
        tokenStorage.setItem('user', JSON.stringify(userData));
        tokenStorage.setItem('organizations', JSON.stringify(action.payload.organizations));
        tokenStorage.setItem(
          'currentOrganization',
          JSON.stringify(action.payload.currentOrganization)
        );
        tokenStorage.setItem('roles', JSON.stringify(action.payload.roles));
        tokenStorage.setItem('permissions', JSON.stringify(action.payload.permissions));
        tokenStorage.setItem('maxRoleLevel', action.payload.maxRoleLevel);

        // No longer storing in localStorage - using sessionStorage only for auto-logout on browser close

        // Initialize WebSocket services for real-time updates
        setTimeout(() => {
          initializeWebSockets(userData);
        }, 500); // Small delay to ensure Redux store is fully updated
      })
      .addCase(login.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // Update user reducers
      .addCase(updateUser.pending, (state) => {
        state.updateStatus = 'loading';
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.updateStatus = 'succeeded';

        // Ensure user data is serializable
        const userData = { ...action.payload };
        if (userData.avatarIcon) {
          delete userData.avatarIcon;
        }

        state.user = { ...state.user, ...userData };
        tokenStorage.setItem('user', JSON.stringify(state.user));
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.updateStatus = 'failed';
        state.error = action.payload;
      })

      // Switch organization reducers
      .addCase(switchOrganization.pending, (state) => {
        state.orgSwitchStatus = 'loading';
      })
      .addCase(switchOrganization.fulfilled, (state, action) => {
        state.orgSwitchStatus = 'succeeded';
        state.currentOrganization = action.payload;
        state.roles = [];
        state.permissions = [];
        state.maxRoleLevel = 0;
        tokenStorage.setItem('currentOrganization', JSON.stringify(action.payload));
      })
      .addCase(switchOrganization.rejected, (state, action) => {
        state.orgSwitchStatus = 'failed';
        state.error = action.payload;
      })

      // Load permissions reducers
      .addCase(loadUserPermissions.pending, (state) => {
        state.permissionsStatus = 'loading';
      })
      .addCase(loadUserPermissions.fulfilled, (state, action) => {
        state.permissionsStatus = 'succeeded';
        state.roles = action.payload.roles;
        state.permissions = action.payload.permissions;
        state.maxRoleLevel = action.payload.maxRoleLevel;
        // Do NOT clobber a session's chosen organisation. Switching is per-session
        // and client-side, but the switcher refreshes permissions straight after,
        // and this used to overwrite the selection with the server's is_primary
        // org — so picking a second organisation snapped instantly back to the
        // first. Only adopt the server's value when the current one is unset or
        // no longer a membership.
        if (action.payload.currentOrganization) {
          const chosen = state.currentOrganization;
          const stillAMember =
            chosen &&
            (state.organizations || []).some((org) => String(org.id) === String(chosen.id));
          if (!stillAMember) {
            state.currentOrganization = action.payload.currentOrganization;
            tokenStorage.setItem(
              'currentOrganization',
              JSON.stringify(action.payload.currentOrganization)
            );
          }
        }
      })
      .addCase(loadUserPermissions.rejected, (state, action) => {
        state.permissionsStatus = 'failed';
        state.error = action.payload;
      });
  },
});

export const {
  logout,
  setCredentials,
  updateUserProfile,
  clearPermissions,
  setCurrentOrganization,
  setPortalContext,
  clearPortalContext,
  startImpersonation,
  endImpersonation,
} = authSlice.actions;

export default authSlice.reducer;
